import { Time } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EventResource } from 'src/app/utils/interfaces/new/event-resource.interface';
import { EventStatus } from 'src/app/utils/interfaces/new/event-status.interface';
import { EventType } from 'src/app/utils/interfaces/new/event-type.interface';
import { Event } from 'src/app/utils/interfaces/old/agenda-interface';
import { FiltresService } from '../filtres/filtres.service';
import { environment } from 'src/environments/environment';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import * as moment from 'moment';
import { CreationRendezVous } from 'src/app/utils/interfaces/new/CreationRendezVous';
import { RessourceCreationRendezVous } from 'src/app/utils/interfaces/new/RessourceCreationRendezVous';
import { TypeEnum as FinRecurrenceType } from 'src/app/utils/interfaces/new/FinRecurrence';
import { TypeEnum as ParametreMoisType } from 'src/app/utils/interfaces/new/ParametresMois';
import { ProchainCreneau } from 'src/app/utils/interfaces/new/ProchainCreneau';
import { SortEnum } from 'src/app/utils/enums/sort-enum';
import { AgendaPeriode } from 'src/app/utils/interfaces/new/AgendaPeriode';
import { UpdateAgendaPeriode } from 'src/app/utils/interfaces/new/UpdateAgendaPeriode';
import { CreationTypeRendezVous } from 'src/app/utils/interfaces/new/CreationTypeRendezVous';
import { TauxOccupation } from 'src/app/utils/interfaces/new/TauxOccupation';
import { JourFerie } from 'src/app/utils/interfaces/new/JourFerie';
import { Router } from '@angular/router';
import { SnackbarService } from '../snackbar/snackbar.service';

@Injectable({
  providedIn: 'root'
})
export class AgendaService {

  private httpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders(),
    params: undefined
  };

  public eventsCache: Map<string, Event> = new Map();

  constructor(
    private _http: HttpClient,
    private _filtresService: FiltresService,
    private _router: Router,
    private _snack: SnackbarService
  ) { }

  public async getEvents(start: Date, end: Date, ressource: EventResource): Promise<Array<Event>> {
    return new Promise<Array<Event>>(async (resolve, reject) => {
      try {
        const query = {};

        const queryResources = new URLSearchParams();
        queryResources.set(`ressources[0][id]`, ressource.id);
        queryResources.set(`ressources[0][type]`, ressource.superType);

        queryResources.forEach((value, key) => {
          query[key] = value;
        });

        query['dateDebut'] = encodeURIComponent(moment.utc(start).toISOString());
        query['dateFin'] = encodeURIComponent(moment.utc(end).toISOString());

        const eventsPaginated = await this._getEvents({ perpage: -1, currentPage: 1 }, query);
        this.repopulateEventCache(eventsPaginated.objects);
        resolve(eventsPaginated.objects);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  public async getEventsForPrint(start: Date, end: Date, ressource: EventResource): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
      try {
        const query = {};

        const queryResources = new URLSearchParams();
        queryResources.set(`ressources[0][id]`, ressource.id);
        queryResources.set(`ressources[0][type]`, ressource.superType);

        queryResources.forEach((value, key) => {
          query[key] = value;
        });

        query['dateDebut'] = encodeURIComponent(moment.utc(start).toISOString());
        query['dateFin'] = encodeURIComponent(moment.utc(end).toISOString());

        const events = await this._getEventsPrint({ perpage: -1, currentPage: 1 }, query);
        resolve(events);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  public async getEventById(eventId: string): Promise<Event> {
    return new Promise<Event>(async (resolve, reject) => {
      try {
        const res = await this._http.get(`${environment.basePath}/agenda/events/${eventId}`, this.httpOptions).toPromise();
        const event = res.body as Event;

        if (event) {
          this.repopulateEventCache([event]);
          resolve(event);
        } else {
          reject('Aucun rendez-vous trouvé');
        }
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  public async getUnplannedEvents(ressource: EventResource): Promise<Event[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const query = {};

        const queryResources = new URLSearchParams();
        queryResources.set(`ressources[0][id]`, ressource.id);
        queryResources.set(`ressources[0][type]`, ressource.superType);

        queryResources.forEach((value, key) => {
          query[key] = value;
        });

        query['aPlanifier'] = true;

        const eventsPaginated = await this._getEvents({ perpage: -1, currentPage: 1 }, query);
        resolve(eventsPaginated.objects);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  public async getResources(): Promise<Array<EventResource>> {
    return new Promise<Array<EventResource>>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        const res = await this._http.get(`${environment.basePath}/user/resources`, this.httpOptions).toPromise();
        const resources = res.body as Array<EventResource>;
        resolve(resources);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  public async createResource(resource: EventResource): Promise<EventResource> {
    return new Promise<EventResource>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        const res = await this._http.post<EventResource>(`${environment.basePath}/user/resources`, resource, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  public async updateResource(resourceId: string, resource: EventResource): Promise<EventResource> {
    return new Promise<EventResource>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        const res = await this._http.put<EventResource>(`${environment.basePath}/user/resources/${resourceId}`, resource, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  public async deleteResource(resourceId: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        const res = await this._http.delete(`${environment.basePath}/user/resources/${resourceId}`, this.httpOptions).toPromise();
        resolve();
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  public async createEvent(event: CreationRendezVous, forceCreate = false, dryrun = false): Promise<Event | void> {
    return new Promise<Event | void>(async (resolve, reject) => {
      try {
        if (event.recurrence) {
          event.recurrence.dateDebutInitiale = event.dateDebut;
          event.recurrence.dateFinInitiale = event.dateFin;
        }

        const eventRes = await this._createEvent(event, forceCreate, dryrun);
        if (!dryrun) {
          resolve(eventRes as Event);
        } else {
          resolve();
        }
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  public async updateEvent(event: Event, forceUpdate = false, handleRecurrence?: 'nextOnly' | 'all', dryrun = false): Promise<Event | void> {
    return new Promise<Event | void>(async (resolve, reject) => {
      try {
        const resources: Array<RessourceCreationRendezVous> = [];

        for (const r of event.ressourses) {
          resources.push({
            id: r.id,
            type: r.superType
          });
        }

        if (event.recurrence) {
          event.recurrence.dateDebutInitiale = event.dateDebut;
          event.recurrence.dateFinInitiale = event.dateFin;
        }

        const eventToUpdate: CreationRendezVous & { id: string } = {
          id: event.id,
          dateDebut: event.dateDebut ? event.dateDebut : undefined,
          dateFin: event.dateFin ? event.dateFin : undefined,
          description: event.description ? event.description : undefined,
          patientId: event.patient && event.patient.id ? event.patient.id : undefined,
          planifie: event.planifie,
          rappel: event.rappel && event.rappel.rappeler ? {
            rappeler: event.rappel ? event.rappel.rappeler : false,
            rappelMail: event.rappel.rappelMail ? event.rappel.rappelMail : false,
            rappelSMS: event.rappel.rappelSMS ? event.rappel.rappelSMS : false,
            confirmationMeeting: event.rappel.confirmationMeeting ? event.rappel.confirmationMeeting : false,
            rappelPatientJours: event.rappel.rappelPatientJours ? event.rappel.rappelPatientJours : 0,
            rappelPatientMinutes: event.rappel.rappelPatientMinutes ? event.rappel.rappelPatientMinutes : 0,
            rappelPraticienJours: event.rappel.rappelPraticienJours ? event.rappel.rappelPraticienJours : 0,
            rappelPraticienMinutes: event.rappel.rappelPraticienMinutes ? event.rappel.rappelPraticienMinutes : 0
          } : undefined,
          recurrence: event.recurrence ? {
            id: event.recurrence.id,
            dateDebutInitiale: event.recurrence.dateDebutInitiale,
            dateFinInitiale: event.recurrence.dateFinInitiale,
            delais: event.recurrence.delais,
            finRecurrence: {
              type: event.recurrence.finRecurrence.type,
              date: event.recurrence.finRecurrence.type === FinRecurrenceType.Date ? event.recurrence.finRecurrence.date : undefined,
              nombreOccurrence: event.recurrence.finRecurrence.type === FinRecurrenceType.OcurrenceNumber ? event.recurrence.finRecurrence.nombreOccurrence : undefined
            },
            uniteDeTemps: event.recurrence.uniteDeTemps,
            parametresMois: event.recurrence.parametresMois ? {
              type: event.recurrence.parametresMois.type,
              positionDansLeMois: event.recurrence.parametresMois.type === ParametreMoisType.DayPosition ? event.recurrence.parametresMois.positionDansLeMois : undefined
            } : undefined,
            parametresSemaine: event.recurrence.parametresSemaine ? {
              uniteSemaineJoursDeLaSemaine: event.recurrence.parametresSemaine.uniteSemaineJoursDeLaSemaine
            } : undefined,
            keepDescription: event && event.recurrence && event.recurrence.keepDescription ? true : false,
            keepPatient: event && event.recurrence && event.recurrence.keepPatient ? true : false
          } : undefined,
          ressoursesId: resources,
          statusId: event.status && event.status.id ? event.status.id : undefined,
          typeId: event.type && event.type.id ? event.type.id : undefined
        };

        const eventRes = await this._createEvent(eventToUpdate, forceUpdate, dryrun, handleRecurrence);
        if (!dryrun) {
          resolve(eventRes as Event);
        } else {
          resolve();
        }
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  public async updateEventStatus(eventId: string, statusId: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        this.httpOptions.headers = this.httpOptions.headers.set('date_now', new Date().toISOString());
        this.httpOptions.params = undefined;
        await this._http.put<Event>(`${environment.basePath}/agenda/events/${eventId}/status/${statusId}`, {}, this.httpOptions).toPromise();
        resolve();
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  public async deleteEvent(eventId: string, reccurDelete?: 'coming' | 'previous_coming'): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        this.httpOptions.params = {};

        let handleRecurrence: 'nextOnly' | 'all' | undefined = undefined;
        if (reccurDelete && reccurDelete === 'coming') {
          handleRecurrence = 'nextOnly';
        } else if (reccurDelete && reccurDelete === 'previous_coming') {
          handleRecurrence = 'all';
        }

        if (handleRecurrence) {
          this.httpOptions.params['handleRecurrence'] = handleRecurrence;
        }

        await this._http.delete<Event>(`${environment.basePath}/agenda/events/${eventId}`, this.httpOptions).toPromise();
        resolve();
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  public async getStatus(): Promise<Array<EventStatus>> {
    return new Promise<Array<EventStatus>>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        const res = await this._http.get(`${environment.basePath}/agenda/status`, this.httpOptions).toPromise();
        const status = res.body as Array<EventStatus>;
        resolve(status);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject();
      }
    });
  }

  public async getEventTypes(): Promise<Array<EventType>> {
    return new Promise<Array<EventType>>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        const res = await this._http.get(`${environment.basePath}/agenda/types`, this.httpOptions).toPromise();
        const eventTypes = res.body as Array<EventType>;
        resolve(eventTypes);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject();
      }
    });
  }

  public async createEventType(type: CreationTypeRendezVous): Promise<EventType> {
    return new Promise<EventType>(async (resolve, reject) => {
      try {
        if (type.couleur.includes('#')) {
          type.couleur = type.couleur.split('#')[1];
        }

        this.httpOptions.params = undefined;
        const res = await this._http.post<EventType>(`${environment.basePath}/agenda/types`, type, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject();
      }
    });
  }

  public async updateEventType(typeId: string, type: CreationTypeRendezVous): Promise<EventType> {
    return new Promise<EventType>(async (resolve, reject) => {
      try {
        if (type.couleur.includes('#')) {
          type.couleur = type.couleur.split('#')[1];
        }

        this.httpOptions.params = undefined;
        const res = await this._http.put<EventType>(`${environment.basePath}/agenda/types/${typeId}`, type, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject();
      }
    });
  }

  public async deleteEventType(typeId: string, replaceBy?: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;

        if (replaceBy) {
          this.httpOptions.params = { replaceBy };
        }
        const res = await this._http.delete(`${environment.basePath}/agenda/types/${typeId}`, this.httpOptions).toPromise();
        resolve();
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  public async searchEvents(
    currentPage = 0,
    patientId?: string, description?: string, typeId?: string, resourcesId?: Array<RessourceCreationRendezVous>, dateStart?: Date, dateEnd?: Date,
    timeStart?: Time, timeEnd?: Time, statusId?: string, noPatientOnly = false, unplannedEventsOnly = false, perPage = 30, sortOrder = SortEnum.ASC, sortBy = 'dateDebut',
  ): Promise<Paginated<Event>> {
    return new Promise<Paginated<Event>>(async (resolve, reject) => {
      try {
        const filtre: Filtres = {
          currentPage,
          perpage: perPage,
          sortOrder,
          sortBy
        }

        const query = {};

        if (patientId && patientId !== '') {
          query['patientId'] = patientId;
        }

        if (description && description !== '') {
          query['description'] = description;
        }

        if (typeId && typeId !== '') {
          query['typeId'] = typeId;
        }

        if (resourcesId && resourcesId.length > 0) {
          const queryResources = new URLSearchParams();
          for (let i = 0; i < resourcesId.length; i++) {
            queryResources.set(`ressources[${i}][id]`, resourcesId[i].id);
            queryResources.set(`ressources[${i}][type]`, resourcesId[i].type);
          }

          queryResources.forEach((value, key) => {
            query[key] = value;
          });
        }

        if (dateStart) {
          if (timeStart) {
            query['dateDebut'] = encodeURIComponent(moment(moment(dateStart).format('YYYY-MM-DD') + ' ' + timeStart + ':00').toISOString());
          } else {
            query['dateDebut'] = encodeURIComponent(moment(moment(dateStart).startOf('day').toString()).toISOString());
          }
        }

        if (dateEnd) {
          if (timeEnd) {
            query['dateFin'] = encodeURIComponent(moment(moment(dateEnd).format('YYYY-MM-DD') + ' ' + timeEnd + ':00').toISOString());
          } else {
            query['dateFin'] = encodeURIComponent(moment(moment(dateEnd).endOf('day').toString()).toISOString());
          }
        }

        if (statusId && statusId !== '') {
          query['statutId'] = statusId;
        }

        if (noPatientOnly) {
          query['sansPatient'] = true;
        }

        if (unplannedEventsOnly) {
          query['aPlanifier'] = true;
        }

        const events = await this._getEvents(filtre, query);

        resolve(events);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject();
      }
    });
  }

  public async getNextSlot(date: Date, length?: string, typeId?: string, resource?: EventResource, allOffice = false): Promise<Array<ProchainCreneau>> {
    return new Promise<Array<ProchainCreneau>>(async (resolve, reject) => {
      try {
        if (!resource && !allOffice) {
          return;
        }

        this.httpOptions.headers = this.httpOptions.headers.set('date_now', date.toISOString());
        this.httpOptions.params = {};

        if (length) {
          this.httpOptions.params['slotLength'] = length;
        } else if (typeId) {
          this.httpOptions.params['eventTypeId'] = typeId;
        } else {
          return;
        }

        if (resource) {
          this.httpOptions.params['resourceId'] = resource.id;
          this.httpOptions.params['resourceType'] = resource.superType;
        } else {
          this.httpOptions.params['allOffice'] = true;
        }

        const res = await this._http.get<Array<ProchainCreneau>>(`${environment.basePath}/agenda/next-slot`, this.httpOptions).toPromise();

        resolve(res.body);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  public async getEventHourSplits(): Promise<Array<string>> {
    return new Promise<Array<string>>(async (resolve, reject) => {
      try {
        resolve(['5', '10', '15', '20', '25', '30', '45', '60', '90', '120']);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject();
      }
    });
  }

  public async getAgendaPeriodes(resourceId: string, resourceType: string, periodeStart?: Date, periodeEnd?: Date): Promise<Array<AgendaPeriode>> {
    return new Promise<Array<AgendaPeriode>>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;

        if (periodeStart && periodeEnd) {
          this.httpOptions.params = {
            periodeStart: encodeURIComponent(moment.utc(periodeStart).toISOString()),
            periodeEnd: encodeURIComponent(moment.utc(periodeEnd).toISOString())
          };
        }

        const res = await this._http.get<Array<AgendaPeriode>>(`${environment.basePath}/agenda/periodes/${resourceId}/type/${resourceType}`, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject();
      }
    });
  }

  public async deleteAgendaPeriodes(periodeId: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        const res = await this._http.delete(`${environment.basePath}/agenda/periodes/${periodeId}`, this.httpOptions).toPromise();
        resolve();
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject();
      }
    });
  }

  public async createAgendaPeriodes(periode: UpdateAgendaPeriode): Promise<AgendaPeriode> {
    return new Promise<AgendaPeriode>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;

        if (periode.couleurFond.includes('#')) {
          periode.couleurFond = periode.couleurFond.split('#')[1];
        }

        if (periode.couleurTexte.includes('#')) {
          periode.couleurTexte = periode.couleurTexte.split('#')[1];
        }

        const res = await this._http.post<AgendaPeriode>(`${environment.basePath}/agenda/periodes`, periode, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject();
      }
    });
  }

  public async updateAgendaPeriodes(periodeId: string, periode: UpdateAgendaPeriode): Promise<AgendaPeriode> {
    return new Promise<AgendaPeriode>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;

        if (periode.couleurFond.includes('#')) {
          periode.couleurFond = periode.couleurFond.split('#')[1];
        }

        if (periode.couleurTexte.includes('#')) {
          periode.couleurTexte = periode.couleurTexte.split('#')[1];
        }

        const res = await this._http.put<AgendaPeriode>(`${environment.basePath}/agenda/periodes/${periodeId}`, periode, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject();
      }
    });
  }

  public async getOccupancyRate(resourceId: string, resourceType: string, year?: string): Promise<TauxOccupation> {
    return new Promise<TauxOccupation>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;

        if (year) {
          this.httpOptions.params = { year }
        }

        const res = await this._http.get<TauxOccupation>(`${environment.basePath}/agenda/occupancy-rate/${resourceId}/type/${resourceType}`, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject();
      }
    });
  }

  public async getJoursFeries(year: string): Promise<Array<JourFerie>> {
    return new Promise<Array<JourFerie>>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;

        const res = await this._http.get<Array<JourFerie>>(`${environment.basePath}/agenda/jours-feries/${year}`, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject();
      }
    });
  }

  public doRemoveNavigationParams(): void {
    if (this._router.routerState.snapshot.url.indexOf('/user/agenda') < 0) {
      localStorage.removeItem('agenda_nav_viewdate');
      localStorage.removeItem('agenda_nav_current_resource');
    }
  }

  private async _getEvents(filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
  }, queryParams?: any): Promise<Paginated<Event>> {
    return new Promise<Paginated<Event>>(async (resolve, reject) => {
      try {
        this.httpOptions.headers = this._filtresService.setHeaders(filtre);
        this.httpOptions.params = queryParams;

        const res = await this._http.get(`${environment.basePath}/agenda/events`, this.httpOptions).toPromise();
        const cabinets = res.body as Event[];

        const paginated: Paginated<Event> = {
          page: Number(res.headers.get('x-page')),
          perpage: Number(res.headers.get('x-per-page')),
          total: Number(res.headers.get('x-total')),
          objects: cabinets || []
        };

        resolve(paginated);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  private async _getEventsPrint(filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
  }, queryParams?: any): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
      try {
        this.httpOptions.headers = this._filtresService.setHeaders(filtre);
        this.httpOptions.params = queryParams;

        const res = await this._http.get(`${environment.basePath}/agenda/print`, this.httpOptions).toPromise();
        const cabinets = res.body as string;

        resolve(cabinets);
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  private async _createEvent(event: CreationRendezVous & { id?: string }, force = false, dryrun = false, handleRecurrence?: 'nextOnly' | 'all'): Promise<Event | void> {
    return new Promise<Event | void>(async (resolve, reject) => {
      try {
        this.httpOptions.params = {};

        if (dryrun) {
          this.httpOptions.params['dryrun'] = true;
        }

        if (event.id) {
          if (force) {
            this.httpOptions.params['forceUpdate'] = true;
          }

          if (handleRecurrence) {
            this.httpOptions.params['handleRecurrence'] = handleRecurrence;
          }
          const res = await this._http.put<Event>(`${environment.basePath}/agenda/events/${event.id}`, event, this.httpOptions).toPromise();
          resolve(res.body);
        } else {
          if (force) {
            this.httpOptions.params['forceCreate'] = true;
          }

          const res = await this._http.post<Event>(`${environment.basePath}/agenda/events`, event, this.httpOptions).toPromise();

          if (dryrun && res.status === 202) {
            resolve();
          } else {
            resolve(res.body);
          }
        }
      } catch (e) {
        if (e.error && e.error.message) {
          this._snack.openSnackBar(e.error.message);
        }
        reject(e);
      }
    });
  }

  private repopulateEventCache(events: Event[]) {
    this.eventsCache.clear();
    events.forEach(event => {
      this.eventsCache.set(event.id, event);
    });
  }

}
